import {
  AsyncPipe,
  DatePipe,
  NgClass,
  NgTemplateOutlet,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  type OnInit,
  OnDestroy,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { IconComponent } from '@components/icon/icon.component';
import { ClickOutsideDirective } from '@directives/click-outside.directive';
import {
  InfiniteScrollComponent,
  IResponse,
  NotificationType,
  Page,
  PageQuery,
  UserNotification,
  UserNotiNavigation,
} from '@models/common.model';
import { CommonService } from '@services/common.service';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Observable, map } from 'rxjs';

const NOTI_NAVIGATION: Record<NotificationType, UserNotiNavigation> = {
  [NotificationType.shopSellItem]: {
    params: {},
    routing: '/orders/:id',
  },
};

@Component({
  selector: 'market-header-notification',
  standalone: true,
  imports: [
    InfiniteScrollModule,
    AsyncPipe,
    RouterModule,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    IconComponent,
    ClickOutsideDirective,
  ],
  templateUrl: './header-notification.component.html',
  styleUrl: './header-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderNotificationComponent
  extends InfiniteScrollComponent<UserNotification, PageQuery>
  implements OnInit, OnDestroy
{
  private commonService = inject(CommonService);

  isOpen = signal(false);
  private _unread = signal(0);

  interval: NodeJS.Timeout | undefined;

  get unread() {
    if (!this._unread()) return '';
    return this._unread() > 99 ? '99+' : `${this._unread()}`;
  }

  ngOnInit(): void {
    this.interval = setInterval(() => {
      if (this.isOpen()) return;
      this.query.set(this.getDefaultQuery());
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  override loadData(
    query: PageQuery
  ): Observable<IResponse<Page<UserNotification>>> {
    return this.commonService.searchNotification(query).pipe(
      map(res => {
        res.data.items.forEach(x => {
          const navigation = NOTI_NAVIGATION[x.type];
          if (navigation) {
            navigation.routing = navigation.routing.replace(
              ':id',
              `${x.objectId}`
            );
          }
          x.navigation = navigation;
        });
        this._unread.set(res.data.unread);
        return res;
      })
    );
  }

  openNotiHandler() {
    this.isOpen.set(true);
  }

  markRead(noti: UserNotification) {
    if (noti.read) return;
    this.commonService.readNotification(noti.id).subscribe(() => {
      noti.read = true;
    });
  }
  markAllNoti() {
    this.commonService.readAllNotification().subscribe(() => {
      this.query.set(this.getDefaultQuery());
    });
  }
}
